import React, { useState } from 'react'

import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'

import { UploadImage } from '../UploadImage'
import {
  CheckCardGrid,
  CountBadge,
  FundraiserWizardShell,
  isYes,
  RadioPillGroup,
  ReviewRow,
  ReviewSection,
  SectionCard,
  StatusValue,
  toYesNo,
  ToggleRow,
  WizardFieldGroup,
} from './FundraiserWizardUI'
import { buildFundraiserPagePath, slugifyPathSegment } from './fundraiserUrl'

const CAMPAIGN_TYPE_PILLS = [
  { value: 'Donation Campaign', label: 'Donation' },
  { value: 'Ticket Sales Fundraiser', label: 'Ticket sales' },
  { value: 'Corporate Matching Campaign', label: 'Corporate matching' },
  { value: 'Per-Ticket Pledge Campaign', label: 'Per-ticket pledge' },
  { value: 'Team / Player Fundraiser', label: 'Team / player' },
]

const CONNECTION_PILLS = [
  { value: 'Entire Organization', label: 'Entire organization' },
  { value: 'Specific Event', label: 'Specific event' },
]

const APPLY_FUNDRAISER_PILLS = [
  { value: 'All tickets', label: 'All tickets' },
  { value: 'Selected offers', label: 'Selected offers' },
]

const PARTICIPANT_TYPE_PILLS = [
  { value: 'Player', label: 'Player' },
  { value: 'Team', label: 'Team' },
  { value: 'Class', label: 'Class' },
  { value: 'Club', label: 'Club' },
  { value: 'Department', label: 'Department' },
]

const SPONSORSHIP_TYPE_PILLS = [
  { value: 'Flat Donation', label: 'Flat donation' },
  { value: 'Matching Donation', label: 'Matching' },
  { value: 'Per-Ticket Pledge', label: 'Per-ticket pledge' },
  { value: 'Revenue Share', label: 'Revenue share' },
]

const STATUS_PILLS = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Active', label: 'Active' },
]

export default function FundraiserCampaignWizard({
  campaignUuid,
  currentStep,
  steps,
  formData,
  errors,
  events,
  participantForm,
  bannerImage,
  sponsorLogo,
  submitState,
  connectedEventName,
  organizationName,
  onStepSelect,
  onFieldChange,
  onConnectionTypeChange,
  onToggleArrayValue,
  onParticipantFormChange,
  onAddParticipant,
  onRemoveParticipant,
  onBannerImageChange,
  onSponsorLogoChange,
  onBack,
  onNext,
  onSaveDraft,
  onPublish,
}) {
  const [bannerCoordinates, setBannerCoordinates] = useState()
  const [sponsorLogoCoordinates, setSponsorLogoCoordinates] = useState()

  const currentStepData = steps[currentStep - 1]
  const participantCountLabel =
    formData.participants.length === 1 ? 'added' : 'added'
  const fundraiserPagePath = buildFundraiserPagePath(
    organizationName,
    formData.pageUrlSlug,
    { placeholder: true }
  )

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <WizardFieldGroup
              label="Campaign name"
              hint="required"
              error={errors.campaignName}
            >
              <Form.Control
                value={formData.campaignName}
                isInvalid={Boolean(errors.campaignName)}
                placeholder="e.g. Spring Gala 2025 Fundraiser"
                onChange={(event) =>
                  onFieldChange('campaignName', event.target.value)
                }
              />
            </WizardFieldGroup>

            <WizardFieldGroup
              label="Campaign type"
              hint="required"
              error={errors.campaignType}
            >
              <RadioPillGroup
                name="campaignType"
                value={formData.campaignType}
                options={CAMPAIGN_TYPE_PILLS}
                onChange={(value) => onFieldChange('campaignType', value)}
              />
            </WizardFieldGroup>

            <WizardFieldGroup label="Description" hint="optional">
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Tell supporters what this campaign is about…"
                value={formData.description}
                onChange={(event) =>
                  onFieldChange('description', event.target.value)
                }
              />
            </WizardFieldGroup>

            <div className="fundraiser-wizard__cols-2">
              {formData.campaignType !== 'Donation Campaign' && (
                <WizardFieldGroup
                  label="Goal amount"
                  hint="required"
                  error={errors.goalAmount}
                >
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="$0.00"
                    value={formData.goalAmount}
                    isInvalid={Boolean(errors.goalAmount)}
                    onChange={(event) =>
                      onFieldChange('goalAmount', event.target.value)
                    }
                  />
                </WizardFieldGroup>
              )}

              <WizardFieldGroup label="Status">
                <RadioPillGroup
                  name="status"
                  value={formData.status}
                  options={STATUS_PILLS}
                  onChange={(value) => onFieldChange('status', value)}
                />
              </WizardFieldGroup>
            </div>

            <div className="fundraiser-wizard__cols-2">
              <WizardFieldGroup label="Start date" hint="optional">
                <Form.Control
                  type="date"
                  value={formData.startDate}
                  onChange={(event) =>
                    onFieldChange('startDate', event.target.value)
                  }
                />
              </WizardFieldGroup>
              <WizardFieldGroup label="End date" hint="optional">
                <Form.Control
                  type="date"
                  value={formData.endDate}
                  onChange={(event) =>
                    onFieldChange('endDate', event.target.value)
                  }
                />
              </WizardFieldGroup>
            </div>
          </>
        )

      case 2:
        return (
          <>
            <WizardFieldGroup label="Connection scope">
              <RadioPillGroup
                name="connectionType"
                value={formData.connectionType}
                options={CONNECTION_PILLS}
                onChange={onConnectionTypeChange}
              />
            </WizardFieldGroup>

            {formData.connectionType === 'Specific Event' && (
              <>
                <WizardFieldGroup
                  label="Event"
                  hint="required"
                  error={errors.eventUuid}
                >
                  <Form.Select
                    value={formData.eventUuid}
                    isInvalid={Boolean(errors.eventUuid)}
                    onChange={(event) =>
                      onFieldChange('eventUuid', event.target.value)
                    }
                  >
                    {events.length === 0 ? (
                      <option value="">No events available</option>
                    ) : (
                      events.map((event) => (
                        <option key={event?.uuid} value={event?.uuid}>
                          {event?.name ||
                            event?.eventName ||
                            event?.title ||
                            'Untitled Event'}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </WizardFieldGroup>

                <WizardFieldGroup label="Apply fundraiser to">
                  <RadioPillGroup
                    name="applyFundraiserTo"
                    value={formData.applyFundraiserTo}
                    options={APPLY_FUNDRAISER_PILLS}
                    onChange={(value) =>
                      onFieldChange('applyFundraiserTo', value)
                    }
                  />
                </WizardFieldGroup>

                <SectionCard title="Donation prompt during checkout">
                  <ToggleRow
                    id="donation-prompt-checkout"
                    label="Show donation prompt during checkout"
                    checked={isYes(formData.donationPromptDuringCheckout)}
                    onChange={(checked) =>
                      onFieldChange(
                        'donationPromptDuringCheckout',
                        toYesNo(checked)
                      )
                    }
                  />
                  {isYes(formData.donationPromptDuringCheckout) && (
                    <WizardFieldGroup label="Default donation amounts">
                      <CheckCardGrid
                        options={['$5', '$10', '$25', 'Custom']}
                        selectedValues={formData.defaultDonationAmounts}
                        onToggle={(amount) =>
                          onToggleArrayValue('defaultDonationAmounts', amount)
                        }
                      />
                    </WizardFieldGroup>
                  )}
                </SectionCard>
              </>
            )}
          </>
        )

      case 3:
        return (
          <>
            <ToggleRow
              id="enable-participant-fundraising"
              label="Enable participant fundraising"
              description="Let individuals or teams raise on behalf of this campaign"
              checked={isYes(formData.enableParticipantFundraising)}
              onChange={(checked) =>
                onFieldChange('enableParticipantFundraising', toYesNo(checked))
              }
            />

            {isYes(formData.enableParticipantFundraising) && (
              <>
                <WizardFieldGroup label="Participant type" className="mt-3">
                  <RadioPillGroup
                    name="participantType"
                    value={formData.participantType}
                    options={PARTICIPANT_TYPE_PILLS}
                    onChange={(value) =>
                      onFieldChange('participantType', value)
                    }
                  />
                </WizardFieldGroup>

                <SectionCard
                  title="Participants"
                  badge={
                    formData.participants.length > 0 ? (
                      <CountBadge
                        count={formData.participants.length}
                        label={participantCountLabel}
                      />
                    ) : null
                  }
                >
                  <div className="fundraiser-wizard__cols-2 mb-3">
                    <WizardFieldGroup label="Name">
                      <Form.Control
                        value={participantForm.name}
                        onChange={(event) =>
                          onParticipantFormChange((prevState) => ({
                            ...prevState,
                            name: event.target.value,
                          }))
                        }
                      />
                    </WizardFieldGroup>
                    <WizardFieldGroup label="Email">
                      <Form.Control
                        type="email"
                        value={participantForm.email}
                        onChange={(event) =>
                          onParticipantFormChange((prevState) => ({
                            ...prevState,
                            email: event.target.value,
                          }))
                        }
                      />
                    </WizardFieldGroup>
                  </div>
                  <WizardFieldGroup label="Goal">
                    <Form.Control
                      value={participantForm.goal}
                      placeholder="$500"
                      onChange={(event) =>
                        onParticipantFormChange((prevState) => ({
                          ...prevState,
                          goal: event.target.value,
                        }))
                      }
                    />
                  </WizardFieldGroup>

                  <button
                    type="button"
                    className="fundraiser-wizard__add-row-btn"
                    onClick={onAddParticipant}
                  >
                    + Add participant
                  </button>

                  {errors.participants && (
                    <div className="error small mt-2">
                      {errors.participants}
                    </div>
                  )}

                  <Table
                    responsive
                    className="fundraiser-wizard__participants-table mt-3 mb-0"
                  >
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Goal</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {formData.participants.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-muted text-center">
                            No participants added yet.
                          </td>
                        </tr>
                      ) : (
                        formData.participants.map((participant, index) => (
                          <tr key={`${participant.email}-${index}`}>
                            <td>{participant.name}</td>
                            <td>{participant.email || '—'}</td>
                            <td>{participant.goal || '—'}</td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0"
                                onClick={() => onRemoveParticipant(index)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </SectionCard>

                <ToggleRow
                  id="allow-public-participant-pages"
                  label="Allow public participant pages"
                  description="Each participant gets a shareable fundraising page"
                  checked={isYes(formData.allowPublicParticipantPages)}
                  onChange={(checked) =>
                    onFieldChange(
                      'allowPublicParticipantPages',
                      toYesNo(checked)
                    )
                  }
                />
                <ToggleRow
                  id="show-leaderboard"
                  label="Show leaderboard"
                  description="Display top fundraisers on the public page"
                  checked={isYes(formData.showLeaderboard)}
                  onChange={(checked) =>
                    onFieldChange('showLeaderboard', toYesNo(checked))
                  }
                />
              </>
            )}
          </>
        )

      case 4:
        return (
          <>
            <ToggleRow
              id="add-corporate-sponsors"
              label="Add corporate sponsors"
              description="Enable sponsor matching or pledges for this campaign"
              checked={isYes(formData.addCorporateSponsors)}
              onChange={(checked) =>
                onFieldChange('addCorporateSponsors', toYesNo(checked))
              }
            />

            {isYes(formData.addCorporateSponsors) && (
              <>
                <SectionCard
                  title={formData.companyName || 'Sponsor details'}
                  badge={
                    formData.sponsorshipType ? (
                      <Badge
                        bg="success"
                        className="fundraiser-wizard__count-badge"
                      >
                        {formData.sponsorshipType}
                      </Badge>
                    ) : null
                  }
                  className="mt-3"
                >
                  <WizardFieldGroup
                    label="Company name"
                    hint="required"
                    error={errors.companyName}
                  >
                    <Form.Control
                      value={formData.companyName}
                      isInvalid={Boolean(errors.companyName)}
                      onChange={(event) =>
                        onFieldChange('companyName', event.target.value)
                      }
                    />
                  </WizardFieldGroup>

                  <WizardFieldGroup
                    label="Sponsorship type"
                    hint="required"
                    error={errors.sponsorshipType}
                  >
                    <RadioPillGroup
                      name="sponsorshipType"
                      value={formData.sponsorshipType}
                      options={SPONSORSHIP_TYPE_PILLS}
                      onChange={(value) =>
                        onFieldChange('sponsorshipType', value)
                      }
                    />
                  </WizardFieldGroup>

                  {formData.sponsorshipType === 'Flat Donation' && (
                    <WizardFieldGroup
                      label="Donation amount"
                      error={errors.donationAmount}
                    >
                      <Form.Control
                        value={formData.donationAmount}
                        placeholder="$5,000"
                        isInvalid={Boolean(errors.donationAmount)}
                        onChange={(event) =>
                          onFieldChange('donationAmount', event.target.value)
                        }
                      />
                    </WizardFieldGroup>
                  )}

                  {formData.sponsorshipType === 'Matching Donation' && (
                    <div className="fundraiser-wizard__cols-2">
                      <WizardFieldGroup label="Match amount">
                        <Form.Control
                          value={formData.matchAmount}
                          onChange={(event) =>
                            onFieldChange('matchAmount', event.target.value)
                          }
                        />
                      </WizardFieldGroup>
                      <WizardFieldGroup
                        label="Maximum match"
                        error={errors.maximumMatch}
                      >
                        <Form.Control
                          value={formData.maximumMatch}
                          isInvalid={Boolean(errors.maximumMatch)}
                          onChange={(event) =>
                            onFieldChange('maximumMatch', event.target.value)
                          }
                        />
                      </WizardFieldGroup>
                    </div>
                  )}

                  {formData.sponsorshipType === 'Per-Ticket Pledge' && (
                    <div className="fundraiser-wizard__cols-2">
                      <WizardFieldGroup
                        label="Amount per ticket"
                        error={errors.amountPerTicket}
                      >
                        <Form.Control
                          value={formData.amountPerTicket}
                          isInvalid={Boolean(errors.amountPerTicket)}
                          onChange={(event) =>
                            onFieldChange('amountPerTicket', event.target.value)
                          }
                        />
                      </WizardFieldGroup>
                      <WizardFieldGroup label="Maximum contribution">
                        <Form.Control
                          value={formData.maximumContribution}
                          onChange={(event) =>
                            onFieldChange(
                              'maximumContribution',
                              event.target.value
                            )
                          }
                        />
                      </WizardFieldGroup>
                    </div>
                  )}

                  {formData.sponsorshipType === 'Revenue Share' && (
                    <div className="fundraiser-wizard__cols-2">
                      <WizardFieldGroup label="Business offer">
                        <Form.Control
                          value={formData.businessOffer}
                          onChange={(event) =>
                            onFieldChange('businessOffer', event.target.value)
                          }
                        />
                      </WizardFieldGroup>
                      <WizardFieldGroup label="Tracking code">
                        <Form.Control
                          value={formData.trackingCode}
                          onChange={(event) =>
                            onFieldChange('trackingCode', event.target.value)
                          }
                        />
                      </WizardFieldGroup>
                    </div>
                  )}

                  <div className="fundraiser-wizard__cols-2">
                    <WizardFieldGroup label="Contact name" hint="optional">
                      <Form.Control
                        value={formData.contactName}
                        placeholder="Jane Smith"
                        onChange={(event) =>
                          onFieldChange('contactName', event.target.value)
                        }
                      />
                    </WizardFieldGroup>
                    <WizardFieldGroup label="Contact email" hint="optional">
                      <Form.Control
                        type="email"
                        value={formData.contactEmail}
                        placeholder="jane@company.com"
                        onChange={(event) =>
                          onFieldChange('contactEmail', event.target.value)
                        }
                      />
                    </WizardFieldGroup>
                  </div>

                  <WizardFieldGroup label="Logo">
                    <UploadImage
                      selectedImage={sponsorLogo}
                      setSelectedImage={onSponsorLogoChange}
                      coordinates={sponsorLogoCoordinates}
                      setCoordinates={setSponsorLogoCoordinates}
                      skipCrop={false}
                    />
                  </WizardFieldGroup>

                  <WizardFieldGroup label="Website" hint="optional">
                    <Form.Control
                      type="url"
                      placeholder="https://company.com"
                      value={formData.website}
                      onChange={(event) =>
                        onFieldChange('website', event.target.value)
                      }
                    />
                  </WizardFieldGroup>

                  <ToggleRow
                    id="display-sponsor-publicly"
                    label="Display sponsor publicly"
                    checked={isYes(formData.displaySponsorPublicly)}
                    onChange={(checked) =>
                      onFieldChange('displaySponsorPublicly', toYesNo(checked))
                    }
                    className="pt-2"
                  />
                </SectionCard>
              </>
            )}
          </>
        )

      case 5:
        return (
          <>
            <WizardFieldGroup
              label="Page URL slug"
              hint="required"
              error={errors.pageUrlSlug}
            >
              <Form.Control
                value={formData.pageUrlSlug}
                isInvalid={Boolean(errors.pageUrlSlug)}
                placeholder="e.g. spring-2026-drive"
                onChange={(event) =>
                  onFieldChange(
                    'pageUrlSlug',
                    slugifyPathSegment(event.target.value)
                  )
                }
              />
              <div className="fundraiser-wizard__slug-preview">
                Preview: <strong>{fundraiserPagePath}</strong>
              </div>
            </WizardFieldGroup>

            <div className="fundraiser-wizard__cols-2">
              <WizardFieldGroup label="Primary color">
                <div className="fundraiser-wizard__color-field">
                  <Form.Control
                    type="color"
                    value={formData.primaryColor}
                    onChange={(event) =>
                      onFieldChange('primaryColor', event.target.value)
                    }
                  />
                  <Form.Control
                    value={formData.primaryColor}
                    onChange={(event) =>
                      onFieldChange('primaryColor', event.target.value)
                    }
                  />
                </div>
              </WizardFieldGroup>
              <WizardFieldGroup label="Banner image" hint="optional">
                <UploadImage
                  selectedImage={bannerImage}
                  setSelectedImage={onBannerImageChange}
                  coordinates={bannerCoordinates}
                  setCoordinates={setBannerCoordinates}
                  skipCrop
                />
              </WizardFieldGroup>
            </div>

            <div className="fundraiser-wizard__cols-2">
              <WizardFieldGroup label="Primary CTA label">
                <Form.Control
                  value={formData.ctaButton}
                  onChange={(event) =>
                    onFieldChange('ctaButton', event.target.value)
                  }
                />
              </WizardFieldGroup>
              <WizardFieldGroup label="Secondary CTA label">
                <Form.Control
                  value={formData.secondaryButton}
                  onChange={(event) =>
                    onFieldChange('secondaryButton', event.target.value)
                  }
                />
              </WizardFieldGroup>
            </div>

            <SectionCard title="Page toggles">
              <ToggleRow
                id="enable-landing-page-donation"
                label="Landing page donations"
                checked={isYes(formData.enableLandingPageDonation)}
                onChange={(checked) =>
                  onFieldChange('enableLandingPageDonation', toYesNo(checked))
                }
              />
              <ToggleRow
                id="show-goal-progress"
                label="Goal progress bar"
                checked={isYes(formData.showGoalProgress)}
                onChange={(checked) =>
                  onFieldChange('showGoalProgress', toYesNo(checked))
                }
              />
              <ToggleRow
                id="show-recent-donors"
                label="Recent donors"
                checked={isYes(formData.showRecentDonors)}
                onChange={(checked) =>
                  onFieldChange('showRecentDonors', toYesNo(checked))
                }
              />
              <ToggleRow
                id="show-donor-messages"
                label="Donor messages"
                checked={isYes(formData.showDonorMessages)}
                onChange={(checked) =>
                  onFieldChange('showDonorMessages', toYesNo(checked))
                }
              />
              <ToggleRow
                id="show-sponsor-logos"
                label="Sponsor logos"
                checked={isYes(formData.showSponsorLogos)}
                onChange={(checked) =>
                  onFieldChange('showSponsorLogos', toYesNo(checked))
                }
              />
            </SectionCard>
          </>
        )

      case 6:
        return (
          <>
            <ToggleRow
              id="show-donation-in-checkout"
              label="Show donation in checkout"
              description="Prompt buyers to donate when purchasing tickets"
              checked={isYes(formData.showDonationInCheckout)}
              onChange={(checked) =>
                onFieldChange('showDonationInCheckout', toYesNo(checked))
              }
            />

            {isYes(formData.showDonationInCheckout) && (
              <>
                <div className="fundraiser-wizard__cols-2 mt-3">
                  <WizardFieldGroup
                    label="Donation title"
                    hint="required"
                    error={errors.donationTitle}
                  >
                    <Form.Control
                      value={formData.donationTitle}
                      isInvalid={Boolean(errors.donationTitle)}
                      onChange={(event) =>
                        onFieldChange('donationTitle', event.target.value)
                      }
                    />
                  </WizardFieldGroup>
                  <WizardFieldGroup
                    label="Description"
                    hint="required"
                    error={errors.donationDescription}
                  >
                    <Form.Control
                      value={formData.donationDescription}
                      isInvalid={Boolean(errors.donationDescription)}
                      onChange={(event) =>
                        onFieldChange('donationDescription', event.target.value)
                      }
                    />
                  </WizardFieldGroup>
                </div>

                <WizardFieldGroup label="Suggested amounts">
                  <CheckCardGrid
                    options={['$5', '$10', '$25', '$50']}
                    selectedValues={formData.suggestedAmounts}
                    onToggle={(amount) =>
                      onToggleArrayValue('suggestedAmounts', amount)
                    }
                  />
                  {errors.suggestedAmounts && (
                    <div className="error small mt-1">
                      {errors.suggestedAmounts}
                    </div>
                  )}
                </WizardFieldGroup>

                <SectionCard title="Additional options">
                  <ToggleRow
                    id="allow-custom-amount"
                    label="Allow custom amount"
                    checked={isYes(formData.allowCustomAmount)}
                    onChange={(checked) =>
                      onFieldChange('allowCustomAmount', toYesNo(checked))
                    }
                  />
                  <ToggleRow
                    id="allow-anonymous-donation"
                    label="Allow anonymous donations"
                    checked={isYes(formData.allowAnonymousDonation)}
                    onChange={(checked) =>
                      onFieldChange('allowAnonymousDonation', toYesNo(checked))
                    }
                  />
                  <ToggleRow
                    id="allow-donor-message"
                    label="Enable donor messages"
                    checked={isYes(formData.allowDonorMessage)}
                    onChange={(checked) =>
                      onFieldChange('allowDonorMessage', toYesNo(checked))
                    }
                  />
                </SectionCard>

                <div className="small fw-semibold text-muted mb-2">
                  Checkout preview
                </div>
                <div className="fundraiser-wizard__checkout-preview">
                  <h5>{formData.donationTitle || 'Support our fundraiser'}</h5>
                  <p>
                    {formData.donationDescription ||
                      'Add a donation to your order'}
                  </p>
                  <div className="fundraiser-wizard__amount-pills">
                    {formData.suggestedAmounts.map((amount, index) => (
                      <span
                        key={amount}
                        className={`fundraiser-wizard__amount-pill${
                          index === 1
                            ? ' fundraiser-wizard__amount-pill--active'
                            : ''
                        }`}
                      >
                        {amount}
                      </span>
                    ))}
                    {isYes(formData.allowCustomAmount) && (
                      <span className="fundraiser-wizard__amount-pill">
                        Custom
                      </span>
                    )}
                  </div>
                  {isYes(formData.allowDonorMessage) && (
                    <Form.Control
                      size="sm"
                      placeholder="Enter message (optional)"
                      disabled
                    />
                  )}
                </div>
              </>
            )}
          </>
        )

      case 7:
        return (
          <>
            <ReviewSection title="Campaign">
              <ReviewRow label="Name" value={formData.campaignName || '—'} />
              <ReviewRow label="Type" value={formData.campaignType || '—'} />
              <ReviewRow
                label="Goal"
                value={formData.goalAmount ? `$${formData.goalAmount}` : '—'}
              />
              <ReviewRow label="Status" value={formData.status || 'Draft'} />
            </ReviewSection>

            <ReviewSection title="Ticketing & checkout">
              <ReviewRow
                label="Scope"
                value={
                  formData.connectionType === 'Specific Event'
                    ? connectedEventName
                    : formData.connectionType
                }
              />
              <ReviewRow label="Checkout donation">
                <StatusValue enabled={isYes(formData.showDonationInCheckout)} />
              </ReviewRow>
              <ReviewRow
                label="Default amounts"
                value={
                  formData.defaultDonationAmounts.length > 0
                    ? formData.defaultDonationAmounts.join(' · ')
                    : '—'
                }
              />
            </ReviewSection>

            <ReviewSection title="Participants & sponsors">
              <ReviewRow
                label="Participants"
                value={
                  isYes(formData.enableParticipantFundraising)
                    ? `${formData.participants.length} added`
                    : 'Disabled'
                }
              />
              <ReviewRow label="Leaderboard">
                <StatusValue
                  enabled={isYes(formData.showLeaderboard)}
                  enabledLabel="On"
                  disabledLabel="Off"
                />
              </ReviewRow>
              <ReviewRow
                label="Corporate sponsors"
                value={
                  isYes(formData.addCorporateSponsors)
                    ? formData.companyName || '1 sponsor'
                    : 'None'
                }
              />
            </ReviewSection>

            <ReviewSection title="Public page">
              <ReviewRow
                label="URL"
                value={
                  buildFundraiserPagePath(
                    organizationName,
                    formData.pageUrlSlug
                  ) || '—'
                }
              />
              <ReviewRow label="Landing page">
                <StatusValue
                  enabled={isYes(formData.enableLandingPageDonation)}
                />
              </ReviewRow>
              <ReviewRow label="Sponsor logos">
                <StatusValue enabled={isYes(formData.showSponsorLogos)} />
              </ReviewRow>
            </ReviewSection>

            <div className="fundraiser-wizard__info-box">
              Saving as a draft lets you continue editing before going live.
              Publishing makes this campaign visible and active immediately.
            </div>
          </>
        )

      default:
        return null
    }
  }

  return (
    <FundraiserWizardShell
      campaignUuid={campaignUuid}
      currentStep={currentStep}
      steps={steps}
      submitMessage={submitState.message}
      submitStatus={submitState.status}
      isSavingDraft={submitState.isSavingDraft}
      isPublishing={submitState.isPublishing}
      onStepSelect={onStepSelect}
      onBack={onBack}
      onNext={onNext}
      onSaveDraft={onSaveDraft}
      onPublish={onPublish}
    >
      <h3 className="fundraiser-wizard__step-title">{currentStepData.title}</h3>
      <p className="fundraiser-wizard__step-desc">
        {currentStepData.description}
      </p>
      {renderStepContent()}
    </FundraiserWizardShell>
  )
}
